import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import { useAuth } from "../auth/AuthContext";
import {
  Badge,
  Button,
  EmptyState,
  LoadingState,
  SectionHeader,
  IconSearch,
  IconTv,
  IconUsers,
  IconTap,
} from "../ui";
import { toggleFavoriteTournament, isFavoriteTournament } from "../_utils/favoritesStorage";
import TournamentBoard from "./TournamentBoard";

export default function LandingPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [serverNow, setServerNow] = useState(null);
  const [favTick, setFavTick] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadPublic = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { isFinished: false, pageSize: 48, page: 1 };
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await axios.get(`${appConstants.API_URL}/api/public/tournaments`, { params });
      setTournaments(Array.isArray(res.data) ? res.data : []);
      const dateHeader = res.headers?.date || res.headers?.Date;
      const parsed = dateHeader ? new Date(dateHeader) : null;
      setServerNow(parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date());
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los torneos publicos en este momento.");
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadPublic();
  }, [loadPublic]);

  const handleFavoriteClick = (tournament) => {
    if (!user) return;
    toggleFavoriteTournament({ id: tournament.id, name: tournament.name });
    setFavTick((x) => x + 1);
  };

  return (
    <div className="flex flex-col">
      <section className="relative flex min-h-[min(88vh,920px)] items-center overflow-hidden">
        <div className="absolute inset-0 bg-mesh-hero" aria-hidden />
        <div className="pointer-events-none absolute inset-0 bg-noise opacity-70 mix-blend-overlay" aria-hidden />
        <div
          className="pointer-events-none absolute -right-20 top-1/4 h-[min(55vw,420px)] w-[min(55vw,420px)] rounded-full bg-gold/15 blur-[100px] animate-float"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-32 bottom-0 h-[min(70vw,520px)] w-[min(70vw,520px)] rounded-full bg-arena-bright/20 blur-[120px]"
          aria-hidden
        />

        <div className="relative z-10 ui-page-wrap py-16 sm:py-20 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8">
            <div className="space-y-6 animate-fade-up lg:col-span-7">
              <Badge variant="gold" className="uppercase tracking-[0.2em] bg-gold-bright/10 text-gold-bright border-gold-bright/35">
                Gestion total de torneos - mesa en vivo - visor TV
              </Badge>
              <h1 className="font-display text-balance text-[clamp(2.3rem,6vw,3.9rem)] font-extrabold leading-[1.05] text-white drop-shadow-lg">
                Organiza, opera y muestra tu torneo como un evento profesional
              </h1>
              <p className="max-w-xl text-balance text-lg leading-relaxed text-white/85 sm:text-xl">
                Desde grupos y calendario hasta marcador en vivo, bracket y pantalla publica. Todo conectado en un solo flujo.
              </p>
              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap">
                <Button as={RouterLink} to="#torneos-activos" variant="gold" size="lg" className="px-8">
                  Explorar torneos en juego
                </Button>
                <Button as={RouterLink} to="/documentacion" variant="outlineInverse" size="lg" className="px-8">
                  Ver documentacion
                </Button>
                <Button
                  as={RouterLink}
                  to={user ? "/mis-torneos" : "/login"}
                  state={user ? undefined : { from: { pathname: "/mis-torneos" } }}
                  variant="outlineInverse"
                  size="lg"
                  className="px-8"
                >
                  Abrir panel de gestion
                </Button>
                <Button as={RouterLink} to={user ? "/crear-torneo" : "/register"} variant="outlineInverse" size="lg" className="px-8">
                  {user ? "Crear un torneo" : "Crear cuenta de organizador"}
                </Button>
              </div>
            </div>

            <div className="opacity-0 animate-fade-up [animation-delay:160ms] [animation-fill-mode:forwards] lg:col-span-5">
              <div className="rounded-3xl border border-white/15 bg-card-glass p-6 shadow-lift backdrop-blur-xl sm:p-8">
                <p className="mb-6 text-xs font-bold uppercase tracking-widest text-gold-bright/90">Hecho para cada rol del torneo</p>
                <ul className="space-y-6">
                  <li className="flex gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold-bright/15 text-gold-bright">
                      <IconTv />
                    </span>
                    <div>
                      <p className="font-bold text-white">Publico y aficion</p>
                      <p className="mt-1 text-sm leading-relaxed text-white/70">
                        Consulta grupos, partidos y fase final en tiempo real desde movil o pantalla grande.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold-bright/15 text-gold-bright">
                      <IconTap />
                    </span>
                    <div>
                      <p className="font-bold text-white">Organizacion y staff</p>
                      <p className="mt-1 text-sm leading-relaxed text-white/70">
                        Configura equipos, grupos, cruces y colaboradores con control por roles.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold-bright/15 text-gold-bright">
                      <IconUsers />
                    </span>
                    <div>
                      <p className="font-bold text-white">Operadores de mesa</p>
                      <p className="mt-1 text-sm leading-relaxed text-white/70">
                        Inicia partidos, marca goles al instante y cierra resultados con trazabilidad.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="torneos-activos" className="scroll-mt-4 bg-gradient-to-b from-cream to-cream-deep py-14 sm:py-20">
        <div className="ui-page-wrap">
          <SectionHeader
            title="Torneos en juego"
            subtitle="Torneos públicos activos. Cada tarjeta lleva al menú del torneo: grupos, partidos y fase final."
            actions={
              <div className="relative w-full md:max-w-sm">
                <IconSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted" size={18} />
                <input
                  type="search"
                  placeholder="Buscar por nombre, club u organización…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="focus-ring w-full rounded-2xl border border-arena/15 bg-white py-3.5 pl-11 pr-4 text-sm text-ink shadow-[0_2px_12px_-4px_rgba(15,53,49,0.12)] placeholder:text-ink-muted/55"
                  aria-label="Buscar torneos"
                />
              </div>
            }
          />

          {loading ? <LoadingState label="Cargando torneos..." className="min-h-[28vh]" /> : null}

          {error && !loading ? (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>
          ) : null}

          {!loading && !error ? (
            <TournamentBoard
              tournaments={tournaments}
              serverNow={serverNow}
              user={user}
              isFavorite={(t) => isFavoriteTournament(t.id)}
              onFavorite={handleFavoriteClick}
              emptyState={
                <EmptyState
                  title="Aun no hay torneos publicos en juego"
                  description="Si eres organizador, publica tu torneo para que aficion, staff y colaboradores lo sigan desde aqui."
                  action={
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button as={RouterLink} to="/mis-torneos" variant="primary">
                        Ir a mis torneos
                      </Button>
                      <Button as={RouterLink} to="/register" variant="outline">
                        Crear cuenta
                      </Button>
                    </div>
                  }
                />
              }
            />
          ) : null}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-arena/10 py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-cream-deep via-white to-cream opacity-90" aria-hidden />
        <div className="pointer-events-none absolute inset-0 bg-noise opacity-40 mix-blend-multiply" aria-hidden />
        <div className="relative mx-auto max-w-2xl px-4 text-center">
          <h2 className="mb-4 font-display text-3xl font-extrabold text-ink sm:text-4xl">Lleva tu torneo a otro nivel</h2>
          <p className="mb-8 leading-relaxed text-ink-muted">
            Crea el torneo, asigna colaboradores, gestiona marcador en vivo y proyecta resultados para toda la grada desde{" "}
            <RouterLink to="/mis-torneos" className="font-bold text-arena-bright hover:underline">
              Mis torneos
            </RouterLink>{" "}
            y, cuando tu plan lo permita, disena carteleria premium con QR.
          </p>
          <Button as={RouterLink} to={user ? "/mis-torneos" : "/register"} variant="gold" size="lg" className="px-10">
            {user ? "Ir a mis torneos" : "Crear cuenta y empezar"}
          </Button>
        </div>
      </section>
    </div>
  );
}
